import type { AutosaveStatus } from '../../../hooks/useAutosave';

type ClipAutosaveSource = {
  isSaving: boolean;
  savingError?: unknown;
  lastSavedAt?: Date | null;
};

type ClipAutosaveState = {
  status: AutosaveStatus;
  lastSavedAt: Date | null;
};

/**
 * PLAT-11654 — derive the shared `AutosaveStatus` for the clip editor from
 * `ClipVideoCreatorAdapter`, so the clip modal can render the same
 * `AutosaveStatusIcon` / `AutosaveStatusIndicator` pair as Articles.
 *
 * `saved` comes from the adapter's `lastSavedAt`, which is stamped where a
 * save actually persisted. It is NOT inferred from a save run ending without
 * an error: the adapter's save skips (already-in-flight, missing deps) end the
 * same way and persist nothing, and claiming "All changes saved safely" for
 * one of those is worse than making no claim. With no save behind it yet the
 * status stays `idle`, which the icon renders as "Changes are saved
 * automatically".
 *
 * Called in the render path of an `observer`, which is what makes the adapter
 * reads reactive. `source` may be absent while the renderer is still warming
 * up.
 */
export function useClipAutosaveStatus(
  source: ClipAutosaveSource | null | undefined,
): ClipAutosaveState {
  // No adapter yet: the icon still renders, and "Changes are saved
  // automatically" is the honest thing for it to say.
  if (!source) return { status: 'idle', lastSavedAt: null };

  const { isSaving, savingError, lastSavedAt = null } = source;

  if (isSaving) return { status: 'saving', lastSavedAt };
  // The error outlives the run that raised it: the adapter clears it when the
  // next save starts, so the cloud stays red until something is retried.
  if (savingError) return { status: 'error', lastSavedAt };
  return { status: lastSavedAt ? 'saved' : 'idle', lastSavedAt };
}
